import { ApiTypes, GroupNames, TabNames, TabDisplayNames, APIMajorVersions, APIDisplayNames, APINames, StreamingMarketDataV3MajorVersion } from "../variables";
const { default: instrumentBaseExample } = await import('../instrumentBaseExampleData.json', { with: { type: 'json' } });
const { default: entityBaseExample } = await import('../entityBaseExampleData.json', { with: { type: 'json' } });
const { default: listingBaseExample } = await import('../listingBaseExampleData.json', { with: { type: 'json' } });
const { default: marketBaseExample } = await import('../marketBaseExampleData.json', { with: { type: 'json' } });
const { default: endOfDayHistoryExample } = await import('../endOfDayHistoryExampleData.json', { with: { type: 'json' } });
const { default: listingSymbologyExample } = await import('../listingSymbologyExampleData.json', { with: { type: 'json' } });
const { default: instrumentMarketsExample } = await import('../instrumentMarketsExampleData.json', { with: { type: 'json' } });
const { default: instrumentSymbologyExample } = await import('../instrumentSymbologyExampleData.json', { with: { type: 'json' } });

export const apiInfos = [
    {
        apiTitle: APIDisplayNames.InstrumentBase,
        apiName: APINames.instrumentBase,
        group: GroupNames.ReferenceData,
        description: "Basic reference data for instruments",
        versions: [APIMajorVersions.v1, APIMajorVersions.v2],
        overviewDescription: "The Instrument Base service is designed to provide clients with necessary financial instrument reference data for the requested instruments or listings. The interface grants swift and user-friendly access to a predefined set of key attributes applicable to a broad range of asset classes. Its intuitive nature guarantees that clients possess crucial information for enhancing operational efficiency in maintaining their master reference data.",
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.Swagger, displayName: TabDisplayNames.Swagger},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: false,
        example: instrumentBaseExample
    },
    {
        apiTitle: APIDisplayNames.EntityBase,
        apiName: APINames.entityBase,
        group: GroupNames.ReferenceData,
        description: "Basic reference data for entities",
        versions: [APIMajorVersions.v1, APIMajorVersions.v2],
        overviewDescription: "The Entity Base service is designed to provide clients with necessary reference data concerning the requested legal entities or issuer reference data when using the instrument or listing interfaces. This interface grants swift and user-friendly access to a predefined set of key attributes applicable to various entity types. Its intuitive nature guarantees that clients possess crucial information for enhancing operational efficiency in maintaining their master reference data.",
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.Swagger, displayName: TabDisplayNames.Swagger},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: false,
        example: entityBaseExample
    },
    {
        apiTitle: APIDisplayNames.ListingBase,
        apiName: APINames.listingBase,
        group: GroupNames.ReferenceData,
        description: "Basic reference data for listed instruments",
        versions: [APIMajorVersions.v1, APIMajorVersions.v2],
        overviewDescription: "The Listing Base service is designed to provide clients with necessary reference data for the requested listed financial instruments. The interface grants swift and user-friendly access to a predefined set of key attributes applicable to a broad range of asset classes. Its intuitive nature guarantees that clients possess crucial information for enhancing operational efficiency in maintaining their master reference data.",
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.Swagger, displayName: TabDisplayNames.Swagger},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: false,
        example: listingBaseExample
    },
    {
        apiTitle: APIDisplayNames.MarketBase,
        apiName: APINames.marketBase,
        group: GroupNames.ReferenceData,
        description: "Basic reference data for markets",
        versions: [APIMajorVersions.v1, APIMajorVersions.v2],
        overviewDescription: "The Market Base service is designed to provide clients with necessary market reference data for the requested markets or listings. The interface grants swift and user-friendly access to a predefined set of key attributes applicable to various types of price sources. Its intuitive nature guarantees that clients possess crucial information for enhancing operational efficiency in maintaining their master reference data.",
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.Swagger, displayName: TabDisplayNames.Swagger},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: false,
        example: marketBaseExample
    },
    {
        apiTitle: APIDisplayNames.InstrumentSymbology,
        apiName: APINames.instrumentSymbology,
        group: GroupNames.ReferenceData,
        description: "Market and SIX identifier for instruments",
        versions: [APIMajorVersions.v1, APIMajorVersions.v2],
        overviewDescription: "The Instrument Symbology data service is the client's bridge to effortless identifier mapping and dynamic navigation through the robust SIX data model. The service grants the client instant access to both internal and external identifiers, paving the way for innovative new data workflows. Discover a standardized access to comprehensive symbology details across an extensive array of asset classes. It results in data that integrates effortlessly, empowering the clients' systems and applications.",
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.Swagger, displayName: TabDisplayNames.Swagger},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: false,
        example: instrumentSymbologyExample
    },
    {
        apiTitle: APIDisplayNames.ListingSymbology,
        apiName: APINames.listingSymbology,
        group: GroupNames.ReferenceData,
        description: "Market and SIX identifier for listed instruments",
        versions: [APIMajorVersions.v1, APIMajorVersions.v2],
        overviewDescription: "The Listing Symbology data service is the client’s bridge to effortless identifier mapping and dynamic navigation through the robust SIX data model. The service grants the client instant access to both internal and external identifiers, paving the way for innovative new data workflows. Discover a standardized access to comprehensive symbology details across an extensive array of asset classes. It results in data that integrates effortlessly, empowering the clients’ systems and applications.",
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.Swagger, displayName: TabDisplayNames.Swagger},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: false,
        example: listingSymbologyExample
    },
    {
        apiTitle: APIDisplayNames.InstrumentMarkets,
        apiName: APINames.instrumentMarkets,
        group: GroupNames.ReferenceData,
        description: "List of markets where the instrument is listed",
        versions: [APIMajorVersions.v1, APIMajorVersions.v2],
        overviewDescription: "The Instrument Markets API streamlines access to comprehensive information about financial instruments listed on various financial markets. This API offers an efficient means to retrieve detailed insights into each listed financial instrument, along with factors determining why a specific market could be considered the most relevant or optimal for a particular financial instrument. All specified input parameters operate under a conjunctive logic, meaning they must simultaneously meet for a result to be considered a match.",
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.Swagger, displayName: TabDisplayNames.Swagger},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: false,
        example: instrumentMarketsExample
    },
    {
        apiTitle: APIDisplayNames.IndexComposition,
        apiName: APINames.indexComposition,
        group: GroupNames.ReferenceData,
        description: "Index composition for instruments",
        versions: [APIMajorVersions.v1, APIMajorVersions.v2],
        overviewDescription: `<p>Our index composition service offers essential data on index constituents, providing financial institutions, analysts, and developers with accurate, timely information for their investment strategies. Access key details such as the number of constituents, index divisor used for normalization, and a complete list of constituents with identifiers, market codes, share numbers, and weights. Our API is designed for flexibility, reliability, and efficient decision-making in financial markets.</p>`,
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.Swagger, displayName: TabDisplayNames.Swagger},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: true,
        hasUniquesImage: true
    },
    {
        apiTitle: APIDisplayNames.InstrumentClassification,
        apiName: APINames.instrumentClassification,
        group: GroupNames.ReferenceData,
        description: "Categorization reference data for instruments",
        versions: [APIMajorVersions.v1, APIMajorVersions.v2],
        overviewDescription: `The Instrument Classification service contains recognized categorization standards for describing financial instruments. The classification data can be used to provide a concise description of a financial instrument's key features, such as its asset type and other relevant attributes. It is typically used in the trading and investment industry to facilitate the efficient processing and tracking of financial instruments.`,
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.Swagger, displayName: TabDisplayNames.Swagger},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: true
    },
    {
        apiTitle: APIDisplayNames.EntityClassification,
        apiName: APINames.entityClassification,
        group: GroupNames.ReferenceData,
        description: "Categorization reference data for entities",
        versions: [APIMajorVersions.v1, APIMajorVersions.v2],
        overviewDescription: `The Entity Classification service contains recognized categorization standards for describing entities. The classification data can be used to provide a concise description of an entity's industry or market sector or economic activies. Grouping entities into specific categories that reflect their similarities allows for a more effective view and comparison of their functions, operating activities, and business results.`,
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.Swagger, displayName: TabDisplayNames.Swagger},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: true
    },
    {
        apiTitle: APIDisplayNames.InstrumentDetail,
        apiName: APINames.instrumentDetail,
        group: GroupNames.ReferenceData,
        description: "Detailed reference data for instruments",
        versions: [APIMajorVersions.v2],
        overviewDescription: `The Instrument Detail service provides comprehensive reference data for instruments and listings. It builds upon the core attributes delivered by the "Instrument Base" API, offering an expanded dataset that includes detailed asset class characteristics and metadata relevant to the financial instrument.`,
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.Swagger, displayName: TabDisplayNames.Swagger},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: true
    },
    {
        apiTitle: APIDisplayNames.MarketHolidays,
        apiName: APINames.marketHolidays,
        group: GroupNames.ReferenceData,
        description: "Holiday schedule for markets",
        versions: [APIMajorVersions.v2],
        overviewDescription: `The Market Holidays API provides schedules of market holidays and extraordinary trading days, including opening and closing time exceptions. It enables clients to maintain accurate and up‑to‑date trading‑calendar master data across markets.`,
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.Swagger, displayName: TabDisplayNames.Swagger},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: true
    },
    {
        apiTitle: APIDisplayNames.IssueCondition,
        apiName: APINames.issueCondition,
        group: GroupNames.ReferenceData,
        description: "Issue condition for instruments",
        versions: [APIMajorVersions.v2],
        overviewDescription: `<p>The Issue Condition API service for financial instruments is designed to provide seamless access to comprehensive data related to the issuance process. This API delivers an efficient integration and utilization of issuance information, such as:</p>
        <ul>
        <li><strong>Subscription Details: </strong>Access information on subscription types, including the specified period and cut-off date, streamlining the management of subscription processes.</li>
        <li><strong>Payment Information: </strong>Retrieve details on payment types, payment dates, and interest periods, enabling precise handling of financial transactions.</li>
        <li><strong>Details on Payments in Part: </strong>Gain insights into partial payments and denominations available for issue, facilitating flexible and accurate financial instrument management.</li>
        <li><strong>Issue Price, Range, and Taxes: </strong>Obtain critical data on the issue price, price range (if applicable), and fiscal charges associated with the issuance, ensuring comprehensive financial planning.</li>
        <li><strong>Amount and Number of Securities Issued: </strong>Explore minimum and maximum amounts, along with the total number of securities issued.</li>
        </ul>`,
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.Swagger, displayName: TabDisplayNames.Swagger},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: true
    },
    {
        apiTitle: APIDisplayNames.MifidIISuitability,
        apiName: APINames.mifidIISuitability,
        group: GroupNames.InvestorProtection,
        description: "MiFID II suitability for instruments",
        versions: [APIMajorVersions.v2],
        overviewDescription: `MiFID II suitability provides the regulatory investor protection classification of a financial instrument, including general MiFID II indicators and target market definitions. The data encompasses PRIIPs applicability, complexity, leveraged status, distribution of cash, performance‑fee flags, and the target market assessment.
        This dataset enables distributors, asset managers, and financial intermediaries to assess whether a product is suitable for specific client segments and to meet MiFID II product governance obligations.`,
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.Swagger, displayName: TabDisplayNames.Swagger},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: false
    },
    {
        apiTitle: APIDisplayNames.MifidIICostAndCharges,
        apiName: APINames.mifidIICostAndCharges,
        group: GroupNames.InvestorProtection,
        description: "MiFID II cost and charges for instruments.",
        versions: [APIMajorVersions.v2],
        overviewDescription: `The MiFID II Cost and Charges dataset from SIX provides structured information on fees, charges, and cost components associated with financial instruments under MiFID II. This content supports distributors, asset managers, and financial intermediaries in meeting ex‑ante/ex‑post disclosure requirements, investor protection obligations, and ongoing regulatory reporting.`,
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.Swagger, displayName: TabDisplayNames.Swagger},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: false
    },
    {
        apiTitle: APIDisplayNames.StreamingMarketData,
        apiName: APINames.streamingMarketData,
        group: GroupNames.MarketData,
        description: "Real time market data prices for instruments",
        versions: [APIMajorVersions.v1, APIMajorVersions.v2],
        overviewDescription: "Connecting you to the global markets! The multiplicity and variety of trading venues pose a challenge for financial institutions. Our stream of market data prices comes from the broadest range of sources. The data is delivered in real-time on a set of consolidated fields, regardless of where in the world it originates. From wealth management to media organizations, our customers have been using SIX to gain a comprehensive view of global markets for over 90 years.",
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.APIDefinition, displayName: TabDisplayNames.APIDefinition},
            {url: TabNames.Swagger, displayName: TabDisplayNames.Swagger},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: true,
        hasUniquesImage: true
    },
    {
        apiTitle: APIDisplayNames.StreamingMarketDataV3,
        apiName: APINames.streamingMarketDataV3,
        group: GroupNames.MarketData,
        description: "Real time market data prices for instruments",
        beta: true,
        versions: [StreamingMarketDataV3MajorVersion],
        overviewDescription: "Connecting you to the global markets! The multiplicity and variety of trading venues pose a challenge for financial institutions. Our stream of market data prices comes from the broadest range of sources. The data is delivered in real-time on a set of consolidated fields, regardless of where in the world it originates. From wealth management to media organizations, our customers have been using SIX to gain a comprehensive view of global markets for over 90 years.",
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.APIDefinition, displayName: TabDisplayNames.APIDefinition},
            {url: TabNames.WebsocketExplorer, displayName: TabDisplayNames.WebsocketExplorer},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: true
    },
    {
        apiTitle: APIDisplayNames.IntradaySnapshot,
        apiName: APINames.intradaySnapshot,
        group: GroupNames.MarketData,
        description: "Real time snapshots of market data prices for instruments",
        versions: [APIMajorVersions.v1, APIMajorVersions.v2],
        overviewDescription: "Connecting you to the global markets! The multiplicity and variety of trading venues pose a challenge for financial institutions. The Intraday Snapshot captures market data in real time at the point of request on a set of consolidated fields without any further updates. It summarizes current market activity and is allowing someone to synchronize with the real-time data and quickly process key information about market movements. From wealth management to media organizations, our customers have been using SIX to gain a comprehensive view of global markets for over 90 years.",
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.Swagger, displayName: TabDisplayNames.Swagger},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: true
    },
    {
        apiTitle: APIDisplayNames.IntradayHistory,
        apiName: APINames.intradayHistory,
        group: GroupNames.MarketData,
        description: "Intraday historical market data prices for instruments",
        versions: [APIMajorVersions.v1, APIMajorVersions.v2],
        overviewDescription: `<p>Intraday History APIs are used to capture historic intervalized trade market data during a specified time range without further updates. It summarizes market activity and is allowing someone to synchronize with the intraday price data and quickly process key information about market movements. It's often being used for technical analysis and chartering features.</p>
    
        <p>There are two distinct Intraday History endpoints and the request period starts from the specified date ("<code>dateTo</code>") backwards:</p>
        <ul>
        <li><strong>IntradayHistoryTrades: </strong>Retrieve historical intraday trade data, including all trades and best quotes at the moment of the trade, for up to 10 days. Each API request can retrieve data for a maximum of 2 consecutive days of trade history</li>
        <li><strong>IntradayHistorySummary: </strong>Retrieve aggregated price and trading activity data for a specified time period. The data includes the price fields open, high, low, last price, volume, and the number of trades. The data retrieval limitations are as follows:
        <ul>
        <li>If the requested time period is less than 5 minutes, each API request can retrieve data for a maximum of 2 consecutive days, with historical data range up to 10 days</li>
        <li>If the requested time period equals or exceeds 5 minutes, each API request can retrieve data for a maximum of 30 consecutive days, allowing access to historical data range up to 90 days</li>
        </ul>
        </li>
        </ul>`,
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.Swagger, displayName: TabDisplayNames.Swagger},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: true,
        hasUniquesImage: true
    },
    {
        apiTitle: APIDisplayNames.StreamingIntradayTradesWithHistory,
        apiName: APINames.streamingIntradayTradesWithHistory,
        group: GroupNames.MarketData,
        description: "Intraday trade history synchronized & updated with the real time trades for instruments",
        versions: [APIMajorVersions.v1, APIMajorVersions.v2],
        overviewDescription: "Connecting you to the global markets! The multiplicity and variety of trading venues pose a challenge for financial institutions. The Streaming Intraday Trades with History service captures current day trades history at the point of request continued with real time updates. It provides current day trading activity and is allowing someone to synchronize historical trades with real-time updates and quickly process key information about market movements. From wealth management to media organizations, our customers have been using SIX to gain a comprehensive view of global markets for over 90 years.",
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.APIDefinition, displayName: TabDisplayNames.APIDefinition},
            {url: TabNames.Swagger, displayName: TabDisplayNames.Swagger},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: true,
    },
    {
        apiTitle: APIDisplayNames.EndOfDaySnapshot,
        apiName: APINames.endOfDaySnapshot,
        group: GroupNames.MarketData,
        description: "End-of-day market data prices for instruments",
        versions: [APIMajorVersions.v1, APIMajorVersions.v2],
        overviewDescription: "The End-of-Day Snapshot API is designed to deliver accurate and timely price data for a wide range of financial instruments at the point when the trading day concludes or when market-specific embargo times expire. In cases where the market is still open or the embargo time is still in effect at the time of the request, it offers the previous generation of market data prices. This dual functionality ensures clients have access to the most relevant data under different market conditions and to make informed investment decisions and analyze market performance with comprehensive data.",
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.Swagger, displayName: TabDisplayNames.Swagger},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: true
    },
    {
        apiTitle: APIDisplayNames.EndOfDayHistory,
        apiName: APINames.endOfDayHistory,
        group: GroupNames.MarketData,
        description: "Historical market data prices for instruments",
        versions: [APIMajorVersions.v1, APIMajorVersions.v2],
        overviewDescription: "The End of Day History API provides historical price data for instruments. This API is ideal for building candlestick or bar charts over a specific time period.",
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.Swagger, displayName: TabDisplayNames.Swagger},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: false,
        example: endOfDayHistoryExample
    },
    {
        apiTitle: APIDisplayNames.FreeTextSearch,
        apiName: APINames.freeTextSearch,
        group: GroupNames.Search,
        description: "Free text search for instruments, markets, and entities",
        versions: [APIMajorVersions.v1, APIMajorVersions.v2],
        overviewDescription: "The API displays search results for text-based search terms related to financial instruments, entities and markets. The service provides up to 300 results and returning search results that best match the client's query.",
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.Swagger, displayName: TabDisplayNames.Swagger},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: true
    },
    {
        apiTitle: APIDisplayNames.CriteriaSearch,
        apiName: APINames.criteriaSearch,
        group: GroupNames.Search,
        description: "Criteria search for instruments and listings",
        versions: [APIMajorVersions.v2],
        overviewDescription: `<p>The Criteria Search functionality allows users to efficiently retrieve and search for both instruments and listings using user-defined parameters. It enables users to refine results based on specific attributes such as asset class, currency, market, or issuer etc. for precise and relevant results. This functionality is available for both Instrument Criteria Search and Listing Criteria Search, offering flexible filtering capabilities across financial instruments and listings.</p>`,
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.Criteria, displayName: TabDisplayNames.Criteria},
            {url: TabNames.QueryBuilder, displayName: TabDisplayNames.QueryBuilder},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: true
    },
    {
        apiTitle: APIDisplayNames.Estimates,
        apiName: APINames.estimates,
        group: GroupNames.EstimatesAndFundamentals,
        description: "Estimates data for listed instruments from S&P",
        versions: [APIMajorVersions.v1, APIMajorVersions.v2],
        overviewDescription: `
            <p>S&P Estimates is the most comprehensive global estimates based on projections, models, analysis and research. This dataset can be used to evaluate earnings estimates to select stocks and manage investment performance and to track the direction and magnitude of upgrades and downgrades and more.</p>
            <div class="overview-list">
                <p>SIX redistribution of S&P Capital IQ Estimates includes specifically:</p>
                <div class="overview-list-element">
                    <div class="overview-list-icon">
                        <span class="material-icons-outlined">content_copy</span>
                    </div>
                    <div class="overview-list-text">
                        <h4>Consensus Estimates</h4>
                        <ul>
                            <li>Aggregate analyst estimates for public companies</li>
                            <li>A public company's estimates on its own future profit or loss</li>
                        </ul>
                    </div>
                </div>
                <div class="overview-list-element">
                    <div class="overview-list-icon">
                        <span class="material-icons-outlined">fast_rewind</span>
                    </div>
                    <div class="overview-list-text">
                        <h4>Up to 4 years of history</h4>
                        <ul>
                            <li>On top of forecast estimates for specific trading items or companies</li>
                        </ul>
                    </div>
                </div>
                <div class="overview-list-element">
                    <div class="overview-list-icon">
                        <span class="material-icons-outlined">folder</span>
                    </div>
                    <div class="overview-list-text">
                        <h4>Coverage and Consensus Analysis</h4>
                        <ul>
                            <li>Estimated growth rates and estimate surprise calculations</li>
                            <li>Estimates of public companies at any point in time provided by brokers and analysts</li>
                        </ul>
                    </div>
                </div>
                <div class="overview-list-element">
                    <div class="overview-list-icon">
                        <span class="material-icons-outlined">query_stats</span>
                    </div>
                    <div class="overview-list-text">
                        <h4>Estimate Revision</h4>
                        <ul>
                            <li>Upward and downward changes of broker estimates</li>
                        </ul>
                    </div>
                </div>
            </div>
        `,
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.Swagger, displayName: TabDisplayNames.Swagger},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: true,
        hasUniquesImage: true
    },
    {
        apiTitle: APIDisplayNames.Fundamentals,
        apiName: APINames.fundamentals,
        group: GroupNames.EstimatesAndFundamentals,
        description: "Point-in-time Fundamentals data for listed instruments from S&P",
        versions: [APIMajorVersions.v1, APIMajorVersions.v2],
        overviewDescription: `
            <p>S&P Fundamentals provides point-in-time global standardized financial statement data for over 150,000 companies, including over 95,000 active and inactive public companies. It allows you to extend the scope of your historical analysis and back testing models with consistently standardized data from all filings of a company's historical financial periods including press releases, original filings, and all restatements. Clients can request fundamental data on an annual and quarter basis.</p>
            <div class="overview-list">
                <p>SIX redistribution of S&P Capital IQ Financials includes specifically:</p>
                <div class="overview-list-element">
                    <div class="overview-list-icon">
                        <span class="material-icons-outlined">content_copy</span>
                    </div>
                    <div class="overview-list-text">
                        <h4>Access to standardized financials</h4>
                        <ul>
                            <li>Accurate financial information for Global public companies</li>
                        </ul>
                    </div>
                </div>
                <div class="overview-list-element">
                    <div class="overview-list-icon">
                        <span class="material-icons-outlined">account_tree</span>
                    </div>
                    <div class="overview-list-text">
                        <h4>Capital Structure Data</h4>
                        <ul>
                            <li>Detailed debt and equity capital structure data</li>
                        </ul>
                    </div>
                </div>
                <div class="overview-list-element">
                    <div class="overview-list-icon">
                        <span class="material-icons-outlined">fast_rewind</span>
                    </div>
                    <div class="overview-list-text">
                        <h4>Up to 5 years of historical data</h4>
                        <ul>
                            <li>5 years of historical and interim data for &gt;62k public companies</li>
                        </ul>
                    </div>
                </div>
                <div class="overview-list-element">
                    <div class="overview-list-icon">
                        <span class="material-icons-outlined">calendar_today</span>
                    </div>
                    <div class="overview-list-text">
                        <h4>Financial instance date</h4>
                        <ul>
                            <li>Providing the date and time data was first delivered</li>
                        </ul>
                    </div>
                </div>
            </div>
        `,
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.Swagger, displayName: TabDisplayNames.Swagger},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: true,
        hasUniquesImage: true
    },
    {
        apiTitle: APIDisplayNames.SwissStampDuty,
        apiName: APINames.swissStampDuty,
        group: GroupNames.Tax,
        description: "Swiss stamp duty for instruments",
        versions: [APIMajorVersions.v2],
        overviewDescription: "The Swiss Stamp Duty Data Service by SIX provides automated data on securities subject to the Swiss turnover tax (Umsatzabgabe), applicable to transactions involving security dealers. The tax is 0.15% for domestic securities and 0.3% for foreign securities. Banks, financial intermediaries, and entities with securities worth over CHF 10 million qualify as security dealers and are liable for the tax. Certified by the Swiss Federal Tax Administration since 2007, the service covers equities, bonds, and fund units, helping firms meet tax obligations efficiently.",
        type: ApiTypes.WebApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.Swagger, displayName: TabDisplayNames.Swagger},
            {url: TabNames.VersionHistory, displayName: TabDisplayNames.VersionHistory},
        ],
        hasImage: true
    },
    {
        apiTitle: APIDisplayNames.CorporateActions,
        apiName: APINames.corporateActions,
        group: GroupNames.CorporateActions,
        description: "Near Real-Time, Award-Winning SIX Corporate Actions",
        versions: [APIMajorVersions.v1],
        overviewDescription: "Our near real-time, award-winning SIX Corporate Actions are delivered through the SIX Bulk API. Our users can query the SIX Bulk API and follow a very easy to use steps for downloading initial loads or deltas. The Corporate Actions data is delivered in user friendly JSON format, following ISO standards for dates, etc.",
        type: ApiTypes.BulkApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.APIDefinition, displayName: TabDisplayNames.APIDefinition}
        ],
        hasImage: true
    },
    {
        apiTitle: APIDisplayNames.FixedIncomeReferenceDataExtended,
        apiName: APINames.fixedIncomeReferenceDataExtended,
        group: GroupNames.ReferenceData,
        description: "Fixed Income Extended Reference Data for Government & Corporate bonds, US Municipal and Structured Finance",
        versions: [APIMajorVersions.v1],
        overviewDescription: `
            <p>The Fixed Income Reference Data Extended Package builds on the Base offering and is also accessible via the SIX Bulk API, with the same streamlined access to ISO-compliant data in JSON format. It expands coverage across all asset classes and includes daily descriptive data  of 150+fields tailored to user-defined portfolios, along with updates on new issues. Designed to meet diverse portfolio needs, the Extended Package enhances operational efficiency and data accuracy, making it a cost-effective solution for powering financial platforms with minimal effort.</p>
        `,
        type: ApiTypes.BulkApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.APIDefinition, displayName: TabDisplayNames.APIDefinition}
        ],
        hasImage: true
    },
    {
        apiTitle: APIDisplayNames.DigitalAssetsRegulatoryAndTaxServices,
        apiName: APINames.digitalAssetsRegulatoryAndTaxServices,
        group: GroupNames.ReferenceData,
        description: "Digital Assets Regulatory and Tax Services",
        versions: [APIMajorVersions.v1],
        overviewDescription: `
            <p>This Bulk API package includes basic static data related to digital assets, enriched with regulatory and tax indicators. The following asset types are included in the package:</p>
            <ul>
                <li>Crypto Assets</li>
                <li>Traditional financial instruments with exposure to a Digital Asset</li>
                <li>Traditional financial instruments traded on a Digital Ledger Technology</li>
                <li>Instruments with involvement of an institution that has exposure to crypto</li>
            </ul>
            <p>The API delivers regulatory indicators relating to MiFID, the MiCA Regulation, as well as CARF on an instrument level. With the Bulk API package, SIX aims to cover additional regulations and tax related indicators, as they become applicable globally.</p>
        `,
        type: ApiTypes.BulkApi,
        tabs: [
            {url: TabNames.Overview, displayName: TabDisplayNames.Overview},
            {url: TabNames.Content, displayName: TabDisplayNames.Content},
            {url: TabNames.APIDefinition, displayName: TabDisplayNames.APIDefinition}
        ],
        hasImage: true
    }
];
